import { useEffect, useRef, useState } from 'react';

import events from '~/modules/game-events/game-events';
import useQueryParam from '~/modules/hooks/use-query-param';
import useSmoothNavigate from '~/modules/hooks/use-smooth-navigate';
import RemoteMicClient from '~/modules/remote-mic/network/client';
import { transportErrorReason } from '~/modules/remote-mic/network/client/network-client';
import userMediaService from '~/modules/user-media/user-media-service';
import usePermissions from '~/routes/remote-mic/hooks/use-permissions';
import useRemoteMicName from '~/routes/remote-mic/hooks/use-remote-mic-name';
import { ConnectionStatuses } from '~/routes/remote-mic/remote-mic';

import StepAllowMic from './step-allow-mic';
import StepEnterCode from './step-enter-code';
import StepLoadingMic from './step-loading-mic';
import StepSingerAndColour from './step-singer-and-colour';
import WizardFrame from './wizard-frame';

interface Props {
  roomId: string | null;
  connectionStatus: ConnectionStatuses;
  connectionError?: transportErrorReason;
  onWizardComplete: () => void;
}

const STEP_LABELS: Record<number, string> = {
  0: 'Step 1 of 3 · Microphone',
  1: 'Step 2 of 3 · Game code',
  2: 'Step 3 of 3 · Singer & colour',
};

const MIN_CONNECTING_DISPLAY_MS = 1500;
const MIN_MIC_LOADING_DISPLAY_MS = 1500;

// Cached across mounts so switching tabs and coming back does not re-probe and flash the loader.
let cachedInitialStep: number | null = null;

export default function ConnectionWizard({ roomId, connectionStatus, connectionError, onWizardComplete }: Props) {
  // null = probing mic permission, number = the step showing.
  const [currentStep, setCurrentStep] = useState<number | null>(cachedInitialStep);
  const [connectedRoomId, setConnectedRoomId] = useState<string | null>(roomId);
  const connectingStartedAtRef = useRef<number | null>(null);
  const navigate = useSmoothNavigate();
  const permissions = usePermissions();

  const { name: effectiveName, hasStoredName, setName: setPersistedName } = useRemoteMicName();

  const autoConnect = useQueryParam('autoconnect') !== 'false';

  // Mirrors connectionStatus but holds the connecting state on screen for a minimum time, so it is
  // not gone before it is read.
  const [delayedConnectionStatus, setDelayedConnectionStatus] = useState<ConnectionStatuses>(connectionStatus);

  // A phone that knows both its name and its slot has nothing left to answer.
  const finishAfterName = () => {
    const [assignedPlayerNumber] = events.remoteMicPlayerSet.lastParams ?? [null];
    if (permissions !== 'write' || (assignedPlayerNumber !== null && assignedPlayerNumber !== undefined)) {
      onWizardComplete();
    } else {
      setCurrentStep(2);
    }
  };

  const handleConnect = (resolvedRoomId: string) => {
    setConnectedRoomId(resolvedRoomId);
    RemoteMicClient.connect(resolvedRoomId, effectiveName, false);
  };

  const handleName = (finalName: string) => {
    setPersistedName(finalName);
    if (finalName !== effectiveName) {
      RemoteMicClient.renameSelf(finalName);
    }
  };

  useEffect(() => {
    if (cachedInitialStep !== null) {
      setCurrentStep(cachedInitialStep);
      return;
    }
    // Held for a beat before asking, so the loader is seen rather than being overwritten by the
    // browser's own permission prompt; a phone that granted it on a previous visit skips step 1.
    const timer = setTimeout(() => {
      userMediaService
        .requestMicPermission()
        .then(() => {
          cachedInitialStep = 1;
          setCurrentStep(1);
        })
        // Denial is deliberately not cached: re-mounting re-probes, so granting it in the browser's
        // settings and coming back works without a reload.
        .catch(() => setCurrentStep(0));
    }, MIN_MIC_LOADING_DISPLAY_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (connectionStatus === 'connecting' || connectionStatus === 'reconnecting') {
      connectingStartedAtRef.current = Date.now();
      setDelayedConnectionStatus(connectionStatus);
      return;
    }

    const elapsed =
      connectingStartedAtRef.current !== null ? Date.now() - connectingStartedAtRef.current : MIN_CONNECTING_DISPLAY_MS;
    const remaining = MIN_CONNECTING_DISPLAY_MS - elapsed;

    if (remaining <= 0) {
      setDelayedConnectionStatus(connectionStatus);
      return;
    }

    const timer = setTimeout(() => setDelayedConnectionStatus(connectionStatus), remaining);
    return () => clearTimeout(timer);
  }, [connectionStatus]);

  useEffect(() => {
    // A phone that remembers who it is goes straight to its colour, or past it if it has a slot.
    if (currentStep === 1 && delayedConnectionStatus === 'connected') {
      if (hasStoredName) {
        finishAfterName();
      } else {
        setCurrentStep(2);
      }
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [delayedConnectionStatus, currentStep]);

  useEffect(() => {
    if (!roomId && connectedRoomId && delayedConnectionStatus === 'connected') {
      navigate('remote-mic/', { room: connectedRoomId });
    }
  }, [connectedRoomId, delayedConnectionStatus, navigate, roomId]);

  const renderStep = () => {
    switch (currentStep) {
      case null:
        return <StepLoadingMic />;
      case 0:
        return <StepAllowMic onComplete={() => setCurrentStep(1)} />;
      case 1:
        return (
          <StepEnterCode
            roomId={roomId}
            onConnect={handleConnect}
            connectionStatus={delayedConnectionStatus}
            connectionError={connectionError}
            autoConnect={autoConnect}
          />
        );
      case 2:
        return (
          <StepSingerAndColour
            rememberedName={hasStoredName ? effectiveName : null}
            onName={handleName}
            onComplete={onWizardComplete}
          />
        );
      default:
        return null;
    }
  };

  return (
    <WizardFrame
      step={currentStep}
      label={currentStep === null ? STEP_LABELS[0] : (STEP_LABELS[currentStep] ?? '')}
      animationKey={String(currentStep)}>
      {renderStep()}
    </WizardFrame>
  );
}
